import React, { useState, useCallback, useEffect, useRef } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { submitEdiscoverySearch, pollEdiscoveryStatus, fetchEdiscoveryResults } from "../api/client";
import { EDiscoverySearchStatus, UALEvent } from "../types";
import DataTable from "../components/DataTable";
import { useDateFormat } from "../preferences/PreferencesProvider";
import {
  formatDate as formatCalendarDate,
  formatDateTime,
  timezoneAbbreviation,
  zonedWallClockToUtc,
} from "../utils/datetime";
import { csvDocument, downloadCsv } from "../utils/csv";

/**
 * A `datetime-local` value ("2026-09-12T09:00") as a UTC instant.
 *
 * The input gives back a bare wall-clock reading with no zone, and Purview
 * reads the bounds it is given as UTC. Passing the value through meant an
 * auditor in Adelaide who asked for 09:00-17:00 searched 18:30-02:30 of their
 * own day and got a plausible-looking set of records for the wrong period.
 *
 * The API now refuses a timestamp with no zone, so this cannot regress
 * silently; converting here is what makes the picker mean what it shows.
 */
function pickedLocalTimeToUtc(value: string, zone: string): string {
  const [datePart, timePart = "00:00"] = value.split("T");
  const seconds = timePart.split(":").length === 2 ? `${timePart}:00` : timePart;
  return zonedWallClockToUtc(datePart, seconds, zone).toISOString();
}

// Purview's records endpoint takes $top up to 1000. The table shows the first
// page; the download walks the rest, which is what the button has always
// claimed to do.
const CSV_PAGE_SIZE = 1000;

// A hard stop so a runaway nextLink cannot loop forever. Reaching it is
// reported rather than silently truncating the file — see handleDownloadCSV.
const CSV_MAX_ROWS = 200_000;

const COMMON_OPERATIONS = [
  "EditReport",
  "DeleteReport",
  "ViewReport",
  "ExportReport",
  "ShareReport",
  "PublishToWeb",
  "CreateDataset",
  "DeleteDataset",
  "UpdateDataset",
  "ViewDashboard",
  "ShareDashboard",
];

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-400">
      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      <span className="text-sm">{label}</span>
    </div>
  );
}

export default function EDiscoveryPage() {
  const dateFormat = useDateFormat();
  const zone = dateFormat.timezone;
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [filterStartDateTime, setFilterStartDateTime] = useState("");
  const [filterEndDateTime, setFilterEndDateTime] = useState("");
  const [usersText, setUsersText] = useState("");
  const [selectedOps, setSelectedOps] = useState<string[]>([]);
  const [freeTextOps, setFreeTextOps] = useState("");
  const [keywords, setKeywords] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Search job state
  const [jobStatus, setJobStatus] = useState<EDiscoverySearchStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [results, setResults] = useState<UALEvent[]>([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [resultHasMore, setResultHasMore] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvProgress, setCsvProgress] = useState(0);
  const [csvError, setCsvError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  const loadResults = useCallback(async (queryId: string) => {
    setResultsLoading(true);
    setResultsError(null);
    try {
      const data = await fetchEdiscoveryResults(queryId, { limit: 100, offset: 0 });
      setResults(data.records);
      setResultTotal(data.total);
      setResultHasMore(data.hasMore);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load results.";
      setResultsError(msg);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  const startPolling = useCallback((queryId: string) => {
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const status = await pollEdiscoveryStatus(queryId);
        setJobStatus(status);
        if (status.status === "succeeded") {
          stopPolling();
          await loadResults(queryId);
        } else if (status.status === "failed") {
          stopPolling();
        }
      } catch {
        stopPolling();
      }
    }, 5000);
  }, [stopPolling, loadResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setJobStatus(null);
    setResults([]);
    setResultTotal(0);
    setResultHasMore(false);
    stopPolling();

    const userPrincipalNames = usersText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const freeOps = freeTextOps
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const operations = [...selectedOps, ...freeOps];

    setSubmitting(true);
    try {
      const status = await submitEdiscoverySearch({
        displayName: displayName || `Audit Search ${formatCalendarDate(new Date(), dateFormat)}`,
        filterStartDateTime: pickedLocalTimeToUtc(filterStartDateTime, zone),
        filterEndDateTime: pickedLocalTimeToUtc(filterEndDateTime, zone),
        userPrincipalNames: userPrincipalNames.length > 0 ? userPrincipalNames : undefined,
        operations: operations.length > 0 ? operations : undefined,
        keywords: keywords || undefined,
      });
      setJobStatus(status);
      if (status.status === "succeeded") {
        await loadResults(status.queryId);
      } else if (status.status !== "failed") {
        startPolling(status.queryId);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to submit search.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleOp = (op: string) => {
    setSelectedOps((prev) =>
      prev.includes(op) ? prev.filter((o) => o !== op) : [...prev, op]
    );
  };

  // The button said "download CSV for all" and wrote out the hundred rows the
  // table happened to be holding. An auditor comparing a downloaded file
  // against a record count has no way to see the other pages were dropped.
  const handleDownloadCSV = async () => {
    const queryId = jobStatus?.queryId;
    if (!queryId || results.length === 0) return;

    const headers = ["recordId", "creationTime", "eventDate", "userId", "userType", "workload", "operation", "objectId", "clientIp", "resultStatus"];

    setCsvError(null);
    setCsvProgress(0);
    setCsvBusy(true);
    try {
      const collected: UALEvent[] = [];
      let offset = 0;
      let reachedCap = false;
      for (;;) {
        const page = await fetchEdiscoveryResults(queryId, {
          limit: CSV_PAGE_SIZE,
          offset,
        });
        collected.push(...page.records);
        setCsvProgress(collected.length);
        if (collected.length >= CSV_MAX_ROWS) {
          reachedCap = true;
          break;
        }
        if (!page.hasMore || page.records.length === 0) break;
        offset += page.records.length;
      }

      const rows = collected.map((r) =>
        headers.map((h) => (r as unknown as Record<string, unknown>)[h])
      );
      downloadCsv(
        `ediscovery-${queryId}.csv`,
        csvDocument(headers, rows)
      );

      if (reachedCap) {
        // Said out loud rather than left to the row count, because the file
        // itself looks complete.
        setCsvError(
          `This search returned more than ${CSV_MAX_ROWS.toLocaleString()} records. ` +
            `The file contains the first ${collected.length.toLocaleString()} and is NOT ` +
            `a complete extract — narrow the date range or the operation list and run it again.`
        );
      }
    } catch (err: unknown) {
      // No partial file: half an evidence export is worse than none, because
      // nothing in it says which half.
      setCsvError(
        err instanceof Error
          ? `Could not read every page of results, so no file was written: ${err.message}`
          : "Could not read every page of results, so no file was written."
      );
    } finally {
      setCsvBusy(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<UALEvent, any>[] = [
    { accessorKey: "creationTime", header: "Time", cell: ({ getValue }) => formatDate(getValue()) },
    { accessorKey: "userId", header: "User", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "operation", header: "Operation", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workload", header: "Workload", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "objectId", header: "Object", cell: ({ getValue }) => <span className="text-xs font-mono text-gray-500 max-w-xs truncate block" title={getValue()}>{getValue() || "—"}</span> },
    { accessorKey: "clientIp", header: "Client IP", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "resultStatus", header: "Result", cell: ({ getValue }) => getValue() || "—" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">E-Discovery Search</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          On-demand query of Microsoft Purview Audit Logs for long-retention audit data.
        </p>
      </div>

      {/* Info banner */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
        <p>
          Results from this search are <strong>not stored</strong> in the system. They are returned directly from
          Microsoft Purview and reflect data up to Microsoft's retention limit (90 days standard / 1 year with E5 licence).
          For data within the collection window, use the{" "}
          <a href="/audit-log" className="underline font-medium">Audit Log page</a>.
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
        <h2 className="text-base font-semibold text-gray-800">Search Parameters</h2>

        {/* Search name */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Search Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Q1 Audit Review"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
          />
        </div>

        {/* Date range */}
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Start Date / Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={filterStartDateTime}
              onChange={(e) => setFilterStartDateTime(e.target.value)}
              required
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {/* The zone these readings are taken in. The search is submitted
                as UTC, and without this there is nothing on screen to say
                which clock the numbers belong to. */}
            <span className="text-xs text-gray-400">
              {zone} ({timezoneAbbreviation(zone)})
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              End Date / Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={filterEndDateTime}
              onChange={(e) => setFilterEndDateTime(e.target.value)}
              required
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-400">
              {zone} ({timezoneAbbreviation(zone)})
            </span>
          </div>
        </div>

        {/* Users filter */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Users (comma-separated emails, leave blank for all)</label>
          <textarea
            value={usersText}
            onChange={(e) => setUsersText(e.target.value)}
            rows={2}
            placeholder="user1@example.com, user2@example.com"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-xl"
          />
        </div>

        {/* Operations */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Operations</label>
          <div className="flex flex-wrap gap-2">
            {COMMON_OPERATIONS.map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => toggleOp(op)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  selectedOps.includes(op)
                    ? "bg-blue-700 text-white border-blue-700"
                    : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                }`}
              >
                {op}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={freeTextOps}
            onChange={(e) => setFreeTextOps(e.target.value)}
            placeholder="Additional operations (comma-separated)"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
          />
        </div>

        {/* Keywords */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Keywords</label>
          <input
            type="text"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="e.g. sensitive-report"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
          />
        </div>

        {submitError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || polling}
          className="px-5 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
        >
          {submitting ? "Submitting…" : "Submit Search"}
        </button>
      </form>

      {/* Results section */}
      {jobStatus && (
        <div className="space-y-4">
          {/* Status banner */}
          <div className={`rounded-lg border px-4 py-3 text-sm flex items-center gap-3 ${
            jobStatus.status === "succeeded"
              ? "bg-green-50 border-green-200 text-green-800"
              : jobStatus.status === "failed"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-yellow-50 border-yellow-200 text-yellow-800"
          }`}>
            {jobStatus.status === "running" && <Spinner label="" />}
            <div>
              <span className="font-medium capitalize">{jobStatus.status}</span>
              {jobStatus.status === "running" && " — Search running, checking every 5 seconds…"}
              {jobStatus.status === "succeeded" && ` — ${resultTotal.toLocaleString()} record(s) found`}
              {jobStatus.status === "failed" && " — The search job failed. Please try again."}
              {jobStatus.displayName && (
                <span className="ml-2 text-xs opacity-70">({jobStatus.displayName})</span>
              )}
            </div>
          </div>

          {/* Results table */}
          {jobStatus.status === "succeeded" && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {results.length} of {resultTotal.toLocaleString()} records
                  {resultHasMore && " in the table — the CSV download reads every page"}
                </p>
                {results.length > 0 && (
                  <button
                    onClick={handleDownloadCSV}
                    disabled={csvBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    {csvBusy
                      ? `Collecting ${csvProgress.toLocaleString()} record(s)…`
                      : "Download CSV"}
                  </button>
                )}
              </div>

              {csvError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {csvError}
                </div>
              )}

              {resultsLoading && <Spinner label="Loading results…" />}

              {resultsError && !resultsLoading && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {resultsError}
                </div>
              )}

              {!resultsLoading && !resultsError && results.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
                  <p className="font-semibold">No records returned</p>
                  <p className="text-sm">The search completed but found no matching audit records.</p>
                </div>
              )}

              {!resultsLoading && !resultsError && results.length > 0 && (
                <DataTable data={results} columns={columns} initialPageSize={50} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
